import gsap from "gsap"
import { sleep } from "../utils/dom"
import { x, y } from "./CurtainsTransition"

class Curtains {
  constructor() {
    this.currentId = null
    this.introPlayed = false
    this.currentIndex = window.appData?.curtainsCurrentIndex ?? 0
    this.debugMode = false

    this.x = x
    this.y = y
    this.hoverOffset = 320
    this.duration = 0.75
    this.ease = "power2.inOut"
    this.sleepDuration = 1000

    this.isMobile = this.checkMobile()
    this.resizeHandler = this.handleResize.bind(this)
    this.boundHoverHandler = this.handleCurtainHover.bind(this)
    this.boundHoverOutHandler = this.handleCurtainHoverOut.bind(this)
    this.boundPreloadHandler = this.handlePreloadProjectId.bind(this)

    // Store references to elements and listeners for cleanup
    this.leftCurtain = null
    this.rightCurtain = null
    this.menuItems = []
    this.menuItemListeners = new Map()

    // Cache arrays to track preloaded items
    this.preloadedIndexes = []
    this.preloadedProjectIds = []

    window.addEventListener("resize", this.resizeHandler)
    window.addEventListener("preload-project-id", this.boundPreloadHandler)

    this.attachEventListeners()
    this.refreshElements()

    requestAnimationFrame(() => {
      this.onPageLoad()
    })

    window.H.on("NAVIGATE_IN", () => {
      requestAnimationFrame(() => {
        this.refreshElements()
        this.attachEventListeners()
        this.onPageLoad()
      })
    })
  }

  checkMobile() {
    return window.innerWidth < 1024 // lg breakpoint
  }

  refreshElements() {
    this.leftCurtain = document.querySelector("[data-curtain-left]")
    this.rightCurtain = document.querySelector("[data-curtain-right]")
    this.debugLog(`refreshElements: leftCurtain ${this.leftCurtain ? 'found' : 'MISSING'}, rightCurtain ${this.rightCurtain ? 'found' : 'MISSING'}`)
  }

  attachEventListeners() {
    // Remove old listeners first
    this.removeEventListeners()

    // Get fresh menu items from DOM
    this.menuItems = document.querySelectorAll("[data-hover-project]")

    // Attach new listeners
    this.menuItems.forEach((item) => {
      item.addEventListener("mouseenter", this.boundHoverHandler)
      item.addEventListener("mouseleave", this.boundHoverOutHandler)
      
      // Store reference for cleanup
      this.menuItemListeners.set(item, {
        mouseenter: this.boundHoverHandler,
        mouseleave: this.boundHoverOutHandler
      })
    })
  }

  debugLog(message) {
    if (this.debugMode) {
      console.log(`[Curtains] ${message}`)
    }
  }

  removeEventListeners() {
    // Remove all existing listeners
    this.menuItemListeners.forEach((listeners, item) => {
      item.removeEventListener("mouseenter", listeners.mouseenter)
      item.removeEventListener("mouseleave", listeners.mouseleave)
    })
    this.menuItemListeners.clear()
  }

  handleResize() {
    const wasMobile = this.isMobile
    this.isMobile = this.checkMobile()

    // Cleanup animations if switching between mobile/desktop
    if (wasMobile !== this.isMobile) {
      gsap.killTweensOf([this.leftCurtain, this.rightCurtain])
      gsap.set([this.leftCurtain, this.rightCurtain], { x: 0, y: 0 })
      requestAnimationFrame(() => {
        this.onPageLoad()
      })
    }
  }

  async onPageLoad() {
    this.debugLog("onPageLoad() called")
    this.debugLog(`introPlayed: ${this.introPlayed}`)
    this.debugLog(`window.appData available: ${!!window.appData}`)
    this.debugLog(`curtains array length: ${window.appData?.curtains?.length ?? 0}`)
    this.debugLog(`projects array length: ${window.appData?.projects?.length ?? 0}`)

    if (
      !this.introPlayed
    ) {
      this.debugLog("First load - setting image and playing intro")
      this.setImage()
      this.intro()
      return
    }

    this.debugLog("Subsequent load - setting image only")
    this.setImage()
    await sleep(this.sleepDuration)

    gsap.set('main', {
      opacity: 1,
    })
  

    if (this.isMobile) {
      // // Mobile: animate Y (top curtain down, bottom curtain up)
      gsap.to(this.leftCurtain, {
        y: this.y,
        duration: this.duration,
        ease: this.ease,
      })
      gsap.to(this.rightCurtain, {
        y: -this.y,
        duration: this.duration,
        ease: this.ease,
      })
    } else {
      // Desktop: animate X (left curtain right, right curtain left)
      gsap.to(this.leftCurtain, {
        x: this.x,
        duration: this.duration,
        ease: this.ease,
      })
      gsap.to(this.rightCurtain, {
        x: -this.x,
        duration: this.duration,
        ease: this.ease,
      })
    }
  }

  setImage() {
    this.debugLog("setImage() called")
    
    // Check for project ID directly from main element (more reliable than body class during navigation)
    const main = document.querySelector("main")
    const projectId = Number(main?.getAttribute("data-project-id"))
    
    this.debugLog(`Found data-project-id: ${projectId}`)
    
    if (projectId) {
      const projectFound = this.setCurtainsFromProject(projectId)
      
      // If project was found and curtains were set, we're done
      if (projectFound) {
        this.debugLog(`Successfully set curtains from project ${projectId}`)
        
        // Preload first randomized curtain for when user navigates away
        const curtains = window.appData?.curtains ?? []
        if (curtains.length > 0) {
          this.preloadCurtainByIndex(this.currentIndex)
        }
        
        return
      } else {
        this.debugLog(`Project ${projectId} not found in projects array, falling back to curtains`)
      }
    }

    const curtains = window.appData?.curtains ?? []
    this.debugLog(`Using curtains array. Length: ${curtains.length}, currentIndex: ${this.currentIndex}`)
    
    if (!curtains.length) {
      this.debugLog("ERROR: No curtains available in window.appData.curtains")
      return
    }

    this.currentId = null

    // Use shuffled array with current index
    const currentCurtain = curtains[this.currentIndex]
    if (currentCurtain) {
      this.debugLog(`Setting curtains from index ${this.currentIndex}`)
      this.renderCurtainPair(currentCurtain?.medias ?? currentCurtain)
      
      // Preload next curtain in sequence
      this.preloadNextCurtain()
      
      // Increment index for next time (wrap around)
      this.currentIndex = (this.currentIndex + 1) % curtains.length
    } else {
      this.debugLog(`ERROR: No curtain found at index ${this.currentIndex}`)
    }
  }

  intro() {
    this.debugLog("Intro started")
    const main = document.querySelector("main")
    const logo = document.querySelector("[data-curtain-image]")

    const timeline = gsap
      .timeline({
        onComplete: () => {
          this.debugLog("Intro complete")
          this.introPlayed = true
          document.documentElement.classList.remove("intro-playing")
        }
      })
      .set([main, this.leftCurtain, this.rightCurtain], { opacity: 0 })

      if (this.isMobile) {
        timeline
          .set(this.leftCurtain, { y: window.innerHeight / 2 })
          .set(this.rightCurtain, { y: -window.innerHeight / 2 })
          .to(this.leftCurtain, {
            y: 0,
            duration: 1,
          }, 1)
          .to(this.rightCurtain, {
            y: 0,
            duration: 1,
          }, 1)
      } else {
        timeline
          .set(this.leftCurtain, { x: window.innerWidth / 2 })
          .set(this.rightCurtain, { x: -window.innerWidth / 2 })
          .to(this.leftCurtain, {
            x: this.x,
            duration: 1,
          }, 1)
          .to(this.rightCurtain, {
            x: -this.x,
            duration: 1,
          }, 1)
      }
      timeline
      .to([this.leftCurtain, this.rightCurtain], {
        opacity: 1,
        duration: 0.5,
        ease: "power2.inOut",
      }, 0.1)
    
      .set(logo, {
       autoAlpha: 0,
      }, 1.25)
      .to(main, {
        opacity: 1,
      }, 1.5)
 
  }

  handleCurtainHover(e) {
    const projectId = Number(e.currentTarget?.getAttribute("data-project-id"))
    this.setCurtainsFromProject(projectId)

    // On desktop, move curtains an additional 100px on hover
    if (!this.isMobile) {
      gsap.to(this.leftCurtain, {
        x: this.x + this.hoverOffset,
        duration: this.duration,
      })
      gsap.to(this.rightCurtain, {
        x: -this.x - this.hoverOffset,
        duration: this.duration,
      })
    }
  }

  handleCurtainHoverOut() {
    // On desktop, restore curtains to their initial position
    if (!this.isMobile) {
      gsap.to(this.leftCurtain, {
        x: this.x,
        duration: this.duration,
      })
      gsap.to(this.rightCurtain, {
        x: -this.x,
        duration: this.duration,
      })
    }
  }

  setCurtainsFromProject(projectId) {
    if (!projectId) {
      this.debugLog(`setCurtainsFromProject: Invalid projectId: ${projectId}`)
      return false
    }
    
    if (this.currentId === projectId) {
      this.debugLog(`setCurtainsFromProject: Already using project ${projectId}, skipping`)
      return true
    }

    const projects = window.appData?.projects ?? []
    this.debugLog(`setCurtainsFromProject: Searching for project ${projectId} in ${projects.length} projects`)
    
    const project = projects.find((item) => item.id === projectId)

    if (!project) {
      this.debugLog(`setCurtainsFromProject: Project ${projectId} not found in projects array`)
      return false
    }

    this.debugLog(`setCurtainsFromProject: Found project ${projectId}, rendering curtains`)
    this.debugLog(`Project medias:`, project.medias)

    const rendered = this.renderCurtainPair(project.medias)
    if (rendered) {
      this.currentId = projectId
      return true
    } else {
      this.debugLog(`setCurtainsFromProject: Failed to render curtains for project ${projectId}`)
      return false
    }
  }

  renderCurtainPair(source) {
    this.debugLog("renderCurtainPair() called")
    this.debugLog("Source:", source)
    
    if (!source) {
      this.debugLog("ERROR: renderCurtainPair - source is null/undefined")
      return false
    }

    if (!this.leftCurtain || !this.rightCurtain) {
      this.debugLog(`ERROR: renderCurtainPair - curtain elements not found. leftCurtain: ${!!this.leftCurtain}, rightCurtain: ${!!this.rightCurtain}`)
      return false
    }

    const leftCurtainHtml = this.extractCurtainHtml(
      source.image_left_curtain
    )
    const rightCurtainHtml = this.extractCurtainHtml(
      source.image_right_curtain
    )

    this.debugLog(`Extracted HTML - left: ${leftCurtainHtml ? 'found' : 'MISSING'}, right: ${rightCurtainHtml ? 'found' : 'MISSING'}`)

    if (!leftCurtainHtml || !rightCurtainHtml) {
      this.debugLog(`ERROR: renderCurtainPair - Missing HTML. left: ${!!leftCurtainHtml}, right: ${!!rightCurtainHtml}`)
      this.debugLog(`Source keys:`, Object.keys(source))
      this.debugLog(`image_left_curtain value:`, source.image_left_curtain)
      this.debugLog(`image_right_curtain value:`, source.image_right_curtain)
      return false
    }

    this.leftCurtain.innerHTML = leftCurtainHtml
    this.rightCurtain.innerHTML = rightCurtainHtml
    
    this.debugLog("Successfully rendered curtains to DOM")
    return true
  }

  extractCurtainHtml(media) {
    if (!media) {
      this.debugLog("extractCurtainHtml: media is null/undefined")
      return null
    }

    this.debugLog(`extractCurtainHtml: media type is ${typeof media}`)

    if (typeof media === "string") {
      try {
        const parsed = JSON.parse(media)
        const html = parsed?.html ?? null
        this.debugLog(`extractCurtainHtml: Parsed JSON string, html ${html ? 'found' : 'MISSING'}`)
        return html
      } catch (error) {
        this.debugLog(`extractCurtainHtml: ERROR parsing JSON string: ${error.message}`)
        this.debugLog(`extractCurtainHtml: Raw string value:`, media.substring(0, 100))
        return null
      }
    }

    if (typeof media === "object") {
      const html = media.html ?? null
      this.debugLog(`extractCurtainHtml: Object media, html ${html ? 'found' : 'MISSING'}`)
      this.debugLog(`extractCurtainHtml: Object keys:`, Object.keys(media))
      return html
    }

    this.debugLog(`extractCurtainHtml: Unsupported media type: ${typeof media}`)
    return null
  }

  preloadNextCurtain() {
    const curtains = window.appData?.curtains ?? []
    if (!curtains.length) {
      return
    }

    const nextIndex = (this.currentIndex + 1) % curtains.length
    this.preloadCurtainByIndex(nextIndex)
  }

  preloadCurtainByIndex(index) {
    const curtains = window.appData?.curtains ?? []
    if (!curtains.length || index < 0 || index >= curtains.length) {
      return
    }

    // Check cache to avoid duplicate preloading
    if (this.preloadedIndexes.includes(index)) {
      // this.debugLog(`Skipping preload for index ${index} (already preloaded)`)
      return
    }

    const curtain = curtains[index]
    if (!curtain) {
      return
    }

    this.debugLog(`Preloading curtain pair at index ${index}`)
    const source = curtain?.medias ?? curtain
    this.preloadCurtainPairFromSource(source)
    
    // Add to cache after preloading
    this.preloadedIndexes.push(index)
  }

  preloadCurtainByProjectId(projectId) {
    if (!projectId) {
      return
    }

    // Check cache to avoid duplicate preloading
    if (this.preloadedProjectIds.includes(projectId)) {
      // this.debugLog(`Skipping preload for project ${projectId} (already preloaded)`)
      return
    }

    const project = window.appData?.projects?.find(
      (item) => item.id === projectId
    )

    if (!project || !project.medias) {
      return
    }

    this.debugLog(`Preloading curtain pair from project ${projectId}`)
    this.preloadCurtainPairFromSource(project.medias)
    
    // Add to cache after preloading
    this.preloadedProjectIds.push(projectId)
  }

  preloadCurtainPairFromSource(source) {
    if (!source) {
      return
    }

    const leftHtml = this.extractCurtainHtml(source.image_left_curtain)
    const rightHtml = this.extractCurtainHtml(source.image_right_curtain)
    
    if (leftHtml) {
      this.preloadImageFromHtml(leftHtml)
    }
    
    if (rightHtml) {
      this.preloadImageFromHtml(rightHtml)
    }
  }

  handlePreloadProjectId(e) {
    const projectId = Number(e.detail)
    if (projectId) {
      this.preloadCurtainByProjectId(projectId)
    }
  }

  preloadImageFromHtml(html) {
    if (!html) {
      return
    }

    // Create a temporary element to parse the HTML
    const temp = document.createElement("div")
    temp.innerHTML = html
    const img = temp.querySelector("img")
    
    if (!img) {
      return
    }

    // Preload using the srcset if available, otherwise use src
    const srcset = img.getAttribute("srcset")
    const src = img.getAttribute("src")
    
    if (srcset) {
      // Create link element for preload with srcset
      const link = document.createElement("link")
      link.rel = "preload"
      link.as = "image"
      link.setAttribute("imagesrcset", srcset)
      
      const sizes = img.getAttribute("sizes")
      if (sizes) {
        link.setAttribute("imagesizes", sizes)
      }
      
      document.head.appendChild(link)
    } else if (src) {
      // Fallback to simple preload if no srcset
      const link = document.createElement("link")
      link.rel = "preload"
      link.as = "image"
      link.href = src
      document.head.appendChild(link)
    }
  }
}

export default Curtains
